import { useQuery } from "@tanstack/react-query";
import { api } from "../../config";

function useSearchProducts(params, enabl) {
    return useQuery({
        queryFn: async () => {
            const res = await api.get(`/product/search`, {params});
            return res.data;
        },
        // Include all relevant params in the query key for correct cache/refetch behavior
        queryKey: [
            'search-products',
            params.page,
            params.limit,
            params.title,
            params.partnerId,
            params.category_id,
            params.minPrice,
            params.maxPrice,
            params.isActive,
            params.minStock,
            params.maxStock,
            // params.sort
        ],
        enabled: enabl,
        retry: false,
        refetchOnWindowFocus: false
    })
}

function useGetGlobalProductInformation(channel, enabl) {
    return useQuery({
        queryFn: async () => {
            const res = await api.get(`/product/global-info`, { 
                params: { channel } 
            });
            return res.data;
        },
        queryKey: ['global-product-information', channel],
        enabled: enabl,
        retry: false,
        refetchOnWindowFocus: false
    })
}

function useUpdateProductPrice(productId, skuId, data, enabl) {
    return useQuery({
        queryFn: async () => {
            const res = await api.put(`/product/${productId}/skus/${skuId}/price`, data);
            return res.data;
        },
        queryKey: ['update-product-price', productId, skuId],
        enabled: enabl,
        retry: false,
        refetchOnWindowFocus: false
    })
}

function useAveragePriceMetrics(params, enabl) {
    return useQuery({
        queryFn: async () => {
            const res = await api.get(`/product/metrics/average-price`, {params});
            return res.data;
        },
        queryKey: ['average-price-metrics', params.startDate, params.endDate, params.channel],
        enabled: enabl,
        retry: false,
        refetchOnWindowFocus: false
    })
}

function useSalesByCategoryMetrics(params, enabl) {
    return useQuery({
        queryFn: async () => {
            const res = await api.get(`/product/metrics/sales-by-category`, {params});
            return res.data;
        },
        queryKey: ['sales-by-category-metrics', params.startDate, params.endDate, params.channel, params.limit],
        enabled: enabl,
        retry: false,
        refetchOnWindowFocus: false
    })
}

function useSalesByPriceRangeMetrics(params, enabl) {
    return useQuery({
        queryFn: async () => {
            const res = await api.get(`/product/metrics/sales-by-price-range`, {params});
            return res.data;
        },
        queryKey: ['sales-by-price-range-metrics', params.startDate, params.endDate, params.channel],
        enabled: enabl,
        retry: false,
        refetchOnWindowFocus: false
    })
}

function useSalesByBrandMetrics(params, enabl) {
    return useQuery({
        queryFn: async () => {
            const res = await api.get(`/product/metrics/sales-by-brand`, {params});
            return res.data;
        },
        queryKey: ['sales-by-brand-metrics', params.startDate, params.endDate, params.channel, params.limit],
        enabled: enabl,
        retry: false,
        refetchOnWindowFocus: false
    })
}

function useCancellationRateMetrics(params, enabl) {
    return useQuery({
        queryFn: async () => {
            const res = await api.get(`/product/metrics/cancellation-rate`, {params});
            return res.data;
        },
        queryKey: ['cancellation-rate-metrics', params.startDate, params.endDate, params.channel],
        enabled: enabl,
        retry: false,
        refetchOnWindowFocus: false
    })
}

function useProductLifecycleMetrics(productId, params, enabl) {
    return useQuery({
        queryFn: async () => {
            const res = await api.get(`/product/metrics/sku/${productId}/lifecycle`, {params});
            return res.data;
        },
        queryKey: ['product-lifecycle-metrics', productId, params.months, params.channel],
        enabled: enabl,
        retry: false,
        refetchOnWindowFocus: false
    })
}

function usePriceVolumeCorrelationMetrics(params, enabl) {
    return useQuery({
        queryFn: async () => {
            const res = await api.get(`/product/metrics/price-volume-correlation`, {params});
            return res.data;
        },
        queryKey: ['price-volume-correlation-metrics', params.startDate, params.endDate, params.channel],
        enabled: enabl,
        retry: false,
        refetchOnWindowFocus: false
    })
}

function useCatalogAgeMetrics(params, enabl) {
    return useQuery({
        queryFn: async () => {
            const res = await api.get(`/product/metrics/catalog-age`, {params});
            return res.data;
        },
        queryKey: ['catalog-age-metrics', params.startDate, params.endDate, params.channel],
        enabled: enabl,
        retry: false,
        refetchOnWindowFocus: false
    })
}

function useSkuPerformanceComparison(partnerId, params, enabl) {
    return useQuery({
        queryFn: async () => {
            const res = await api.get(`/product/metrics/sku/${partnerId}/performance`, {params});
            return res.data;
        },
        queryKey: ['sku-performance-comparison', partnerId, params.startDate, params.endDate, params.channel],
        enabled: enabl,
        retry: false,
        refetchOnWindowFocus: false
    })
}

function useSalesByRegionMetrics(params, enabl) {
    return useQuery({
        queryFn: async () => {
            const res = await api.get(`/product/metrics/sales-by-region`, {params});
            return res.data;
        },
        queryKey: ['sales-by-region-metrics', params.startDate, params.endDate, params.channel],
        enabled: enabl,
        retry: false,
        refetchOnWindowFocus: false
    })
}

function useOrderCompletionTimeMetrics(params, enabl) {
    return useQuery({
        queryFn: async () => {
            const res = await api.get(`/product/metrics/order-completion-time`, {params});
            return res.data;
        },
        queryKey: ['order-completion-time-metrics', params.startDate, params.endDate, params.channel],
        enabled: enabl,
        retry: false,
        refetchOnWindowFocus: false
    })
}

function usePriceReferences(params, enabl, options = {}) {
    return useQuery({
        queryFn: async () => {
            const res = await api.get(`/product/price-references`, {params: {...params, channel: 'MERCADO_LIVRE'}});
            return res.data;
        },
        queryKey: ['price-references', params.page, params.limit, params.item_id, params.minPrice, params.maxPrice],
        enabled: enabl,
        retry: false,
        refetchOnWindowFocus: false,
        keepPreviousData: options.keepPreviousData || false,
        staleTime: options.staleTime || 0,
        ...options
    })
}

export {
    useSearchProducts,
    useUpdateProductPrice,
    useAveragePriceMetrics,
    useSalesByCategoryMetrics,
    useSalesByPriceRangeMetrics,
    useSalesByBrandMetrics,
    useCancellationRateMetrics,
    useProductLifecycleMetrics,
    usePriceVolumeCorrelationMetrics,
    useCatalogAgeMetrics,
    useSkuPerformanceComparison,
    useSalesByRegionMetrics,
    useOrderCompletionTimeMetrics,
    useGetGlobalProductInformation,
    usePriceReferences
}